import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useAnalytics } from '../hooks/useAnalytics';
import { 
  fadeInUpVariants, 
  staggerContainerVariants, 
  staggerItemVariants,
  cardHoverVariants,
  slideInVariants
} from '../utils/animations';
import { Project3DCard } from './Interactive3D';
import { Box, Typography, Card, CardContent, CardMedia, Button, Stack, Chip, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MatrixBackground from './MatrixBackground';

const SectionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden'
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1
}));



const ProjectImage = styled(CardMedia)(({ theme }) => ({
  height: 200,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(0, 102, 204, 0.1), rgba(0, 212, 170, 0.1))',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  '&:hover::after': {
    opacity: 1
  }
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: theme.spacing(6),
  '& .carousel-wrapper': {
    overflow: 'hidden',
    borderRadius: '20px'
  }
}));

const ProjectSlider = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  transition: 'transform 0.5s ease-in-out',
  padding: theme.spacing(1)
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  minWidth: '380px',
  maxWidth: '380px',
  height: '100%',
  borderRadius: '20px',
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    minWidth: '320px',
    maxWidth: '320px'
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '280px',
    maxWidth: '280px'
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
    borderColor: theme.palette.primary.main
  }
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${theme.palette.primary.main}`,
  width: '50px',
  height: '50px',
  zIndex: 2,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    transform: 'translateY(-50%) scale(1.1)'
  },
  '&.prev': {
    left: '-25px'
  },
  '&.next': {
    right: '-25px'
  },
  '&:disabled': {
    opacity: 0.5,
    backgroundColor: theme.palette.grey[200],
    borderColor: theme.palette.grey[300],
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
      transform: 'translateY(-50%)'
    }
  }
}));

interface ProjectProps {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  technologies: string[];
  githubUrl?: string;
  liveUrl?: string;
}

const ProjectItem: React.FC<ProjectProps> = ({ 
  title, 
  description, 
  image, 
  imageAlt, 
  technologies, 
  githubUrl, 
  liveUrl 
}) => {
  const analytics = useAnalytics();
  const [isCardHovered, setIsCardHovered] = useState(false);

  const handleProjectClick = (action: 'github' | 'demo' | 'view', url?: string) => {
    analytics.trackProjectView(title);
    analytics.trackCustomEvent('project_interaction', {
      project_name: title,
      action,
      technologies: technologies.join(', '),
      has_github: !!githubUrl,
      has_demo: !!liveUrl,
      event_category: 'portfolio'
    });
    
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      variants={cardHoverVariants}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
      layout
    >
      <ProjectCard 
        onClick={() => handleProjectClick('view')}
        onMouseEnter={() => setIsCardHovered(true)}
        onMouseLeave={() => setIsCardHovered(false)}
      >
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        >
          {/* 3D Preview when hovered */}
          {isCardHovered ? (
            <Project3DCard 
              isHovered={isCardHovered}
              title={title}
              colors={['#0066cc', '#00d4aa', '#667eea']}
            />
          ) : (
            <ProjectImage
              image={image}
              title={imageAlt}
            />
          )}
        </motion.div>
        
        <CardContent sx={{ p: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h5" gutterBottom fontWeight={600}>
              {title}
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Typography variant="body2" color="text.secondary" paragraph>
              {description}
            </Typography>
          </motion.div>
          
          <motion.div
            variants={staggerContainerVariants}
            initial="hidden"
            animate="visible"
          >
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  variants={staggerItemVariants}
                  whileHover={{ scale: 1.1, rotate: 3 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Chip 
                    label={tech}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </motion.div>
              ))}
            </Stack>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Stack direction="row" spacing={2}>
              <AnimatePresence>
                {githubUrl && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<GitHubIcon />}
                      size="small"
                      sx={{ borderRadius: '20px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectClick('github', githubUrl);
                      }}
                    >
                      GitHub
                    </Button>
                  </motion.div>
                )}
                {liveUrl && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<LaunchIcon />}
                      size="small"
                      sx={{ borderRadius: '20px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectClick('demo', liveUrl);
                      }}
                    >
                      Demo
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </Stack>
          </motion.div>
        </CardContent>
      </ProjectCard>
    </motion.div>
  );
};

const ProjectsSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  // Analytics tracking
  const analytics = useAnalytics();
  
  // Framer Motion visibility tracking
  const isInView = useInView(sectionRef, { 
    once: true, 
    amount: 0.3 
  });
  
  // Projects data - moved to top before useEffects
  const projects: ProjectProps[] = [
    {
      title: 'AI Chatbot Platform',
      description: 'React ve Python kullanarak geliştirdiğim yapay zeka destekli chatbot platformu. Natural language processing ve machine learning teknolojilerini kullanıyor.',
      image: 'https://images.unsplash.com/photo-1587401511935-a7f87afadf2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwZGFzaGJvYXJkJTIwaW50ZXJmYWNlJTIwZGF0YXxlbnwwfDB8fGJsdWV8MTc1NDQ3OTM3NHww&ixlib=rb-4.1.0&q=85',
      imageAlt: 'KOBU Agency on Unsplash',
      technologies: ['React', 'TypeScript', 'Python', 'OpenAI API', 'Node.js'],
      githubUrl: '#',
      liveUrl: '#'
    },
    {
      title: 'DeFi Dashboard',
      description: 'Web3 teknolojileri kullanarak geliştirdiğim DeFi protokollerini takip etmek için dashboard uygulaması. Real-time fiyat takibi ve portfolio yönetimi.',
      image: 'https://images.unsplash.com/photo-1664526937033-fe2c11f1be25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwxfHxibG9ja2NoYWluJTIwY3J5cHRvY3VycmVuY3klMjBuZXR3b3JrJTIwbm9kZXN8ZW58MHwwfHx0ZWFsfDE3NTQ0NzkzNzR8MA&ixlib=rb-4.1.0&q=85',
      imageAlt: 'GuerrillaBuzz on Unsplash',
      technologies: ['React', 'Web3.js', 'Ethereum', 'Solidity', 'Chart.js'],
      githubUrl: '#',
      liveUrl: '#'
    },
    {
      title: 'Smart Contract Analyzer',
      description: 'Blockchain üzerindeki smart contract\'ları analiz eden ve güvenlik açıklarını tespit eden araç. Solidity kod analizi ve risk değerlendirmesi yapıyor.',
      image: 'https://images.unsplash.com/photo-1665152107631-596d33bd00ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwyfHxibG9ja2NoYWluJTIwY3J5cHRvY3VycmVuY3klMjBuZXR3b3JrJTIwbm9kZXN8ZW58MHwwfHx0ZWFsfDE3NTQ0NzkzNzR8MA&ixlib=rb-4.1.0&q=85',
      imageAlt: 'Deng Xiang on Unsplash',
      technologies: ['Python', 'Solidity', 'Web3', 'Flask', 'Machine Learning'],
      githubUrl: '#'
    },
    {
      title: 'Neural Network Visualizer',
      description: 'Yapay sinir ağlarının çalışma prensiplerini görselleştiren interaktif eğitim aracı. Real-time ağırlık değişimlerini ve öğrenme sürecini gösteriyor.',
      image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwxfHxuZXVyYWwlMjBuZXR3b3JrJTIwdmlzdWFsaXphdGlvbnxlbnwwfDB8fGJsdWV8MTc1NDQ3OTM3NHww&ixlib=rb-4.1.0&q=85',
      imageAlt: 'Neural Network Visualization',
      technologies: ['React', 'D3.js', 'TensorFlow.js', 'WebGL', 'TypeScript'],
      githubUrl: '#',
      liveUrl: '#'
    },
    {
      title: 'Decentralized Social Media',
      description: 'IPFS ve Ethereum blockchain kullanarak geliştirdiğim merkezi olmayan sosyal medya platformu. Kullanıcı verilerini blockchain\'de güvenle saklar.',
      image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwxfHxkZWNlbnRyYWxpemVkJTIwbmV0d29yayUyMGJsb2NrY2hhaW58ZW58MHwwfHx0ZWFsfDE3NTQ0NzkzNzR8MA&ixlib=rb-4.1.0&q=85',
      imageAlt: 'Decentralized Network',
      technologies: ['React', 'IPFS', 'Ethereum', 'Solidity', 'MetaMask'],
      githubUrl: '#',
      liveUrl: '#'
    },
    {
      title: 'AI Code Generator',
      description: 'Natural language input\'tan kod üreten yapay zeka aracı. GPT modelleri kullanarak JavaScript, Python ve Solidity kodu otomatik olarak üretiyor.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwxfHxjb2RlJTIwZ2VuZXJhdG9yJTIwYWklMjBwcm9ncmFtbWluZ3xlbnwwfDB8fGJsdWV8MTc1NDQ3OTM3NHww&ixlib=rb-4.1.0&q=85',
      imageAlt: 'AI Code Generation',
      technologies: ['Python', 'OpenAI API', 'React', 'Flask', 'Docker'],
      githubUrl: '#'
    }
  ];
  
  // Update visible cards on window resize
  useEffect(() => {
    const updateVisibleCards = () => {
      const width = window.innerWidth;
      if (width < 768) setVisibleCards(1);
      else if (width < 1200) setVisibleCards(2);
      else setVisibleCards(3);
    };
    
    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);
    
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);
  
  // Reset current index when visible cards change
  useEffect(() => {
    const maxIndex = Math.max(0, projects.length - visibleCards);
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [visibleCards, currentIndex, projects.length]);
  
  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, projects.length - visibleCards);
      const newIndex = prev >= maxIndex ? 0 : prev + 1;
      
      // Track carousel interaction
      analytics.trackCustomEvent('carousel_navigation', {
        direction: 'next',
        from_index: prev,
        to_index: newIndex,
        project_count: projects.length,
        event_category: 'portfolio_interaction'
      });
      
      return newIndex;
    });
  };
  
  const prevSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, projects.length - visibleCards);
      const newIndex = prev <= 0 ? maxIndex : prev - 1;
      
      // Track carousel interaction
      analytics.trackCustomEvent('carousel_navigation', {
        direction: 'prev',
        from_index: prev,
        to_index: newIndex,
        project_count: projects.length,
        event_category: 'portfolio_interaction'
      });
      
      return newIndex;
    });
  };
  
  // Calculate transform offset based on screen size
  const getTransformOffset = () => {
    let cardWidth = 380 + 24; // default card width + gap
    
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 768) cardWidth = 280 + 24; // small screens
      else if (width < 1200) cardWidth = 320 + 24; // medium screens
    }
    
    return -(currentIndex * cardWidth);
  };

  return (
    <SectionContainer ref={sectionRef}>
      <MatrixBackground />
      
      <ContentWrapper>
        <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 4 }}>
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUpVariants}
          >
            <Typography variant="h2" textAlign="center" gutterBottom>
              Projelerim
            </Typography>
          </motion.div>
          
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUpVariants}
            transition={{ delay: 0.2 }}
          >
            <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: '600px', mx: 'auto' }}>
              AI ve Web3 teknolojileri kullanarak geliştirdiğim projeler. Her biri farklı teknolojiler ve yaklaşımlar kullanıyor.
            </Typography>
          </motion.div>
          
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUpVariants}
            transition={{ delay: 0.4 }}
          >
            <CarouselContainer>
              {/* Animated Navigation Buttons */}
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <NavButton 
                  className="prev"
                  onClick={prevSlide}
                  disabled={currentIndex === 0}
                  aria-label="Önceki projeler"
                >
                  <ArrowBackIosIcon />
                </NavButton>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <NavButton 
                  className="next"
                  onClick={nextSlide}
                  disabled={currentIndex >= Math.max(0, projects.length - visibleCards)}
                  aria-label="Sonraki projeler"
                >
                  <ArrowForwardIosIcon />
                </NavButton>
              </motion.div>
              
              {/* Animated Carousel Content */}
              <Box className="carousel-wrapper">
                <motion.div
                  animate={{
                    x: getTransformOffset()
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                >
                  <ProjectSlider ref={sliderRef}>
                    <AnimatePresence mode="wait">
                      {projects.map((project, index) => (
                        <motion.div
                          key={`${project.title}-${index}`}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={slideInVariants('left')}
                          transition={{ delay: index * 0.1 }}
                        >
                          <ProjectItem {...project} />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </ProjectSlider>
                </motion.div>
              </Box>
            </CarouselContainer>
          </motion.div>
            
          {/* Animated Carousel Indicators */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={staggerContainerVariants}
            transition={{ delay: 0.6 }}
          >
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 1, 
              mt: 3 
            }}>
              {Array.from({ length: Math.max(1, projects.length - visibleCards + 1) }).map((_, index) => (
                <motion.div
                  key={index}
                  variants={staggerItemVariants}
                  whileHover={{ scale: 1.3 }}
                  whileTap={{ scale: 0.8 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Box
                    onClick={() => {
                      setCurrentIndex(index);
                      // Track indicator click
                      analytics.trackCustomEvent('carousel_indicator_click', {
                        clicked_index: index,
                        from_index: currentIndex,
                        total_indicators: Math.max(1, projects.length - visibleCards + 1),
                        event_category: 'portfolio_interaction'
                      });
                    }}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: currentIndex === index ? 'primary.main' : 'grey.300',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: currentIndex === index ? 'primary.dark' : 'primary.light'
                      }
                    }}
                  />
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Box>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ProjectsSection;